<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  actionOptions,
  conditionAttributeOptions,
  conditionAttributeSchema,
  directionOptions,
  eventOptions,
  labelOptions,
} from './contactAutomationOptions';

const emit = defineEmits(['close']);
const store = useStore();
const getters = useStoreGetters();

const accountId = computed(() => getters.getCurrentAccountId.value);
const pipelines = computed(() => store.getters['pipelines/getPipelines'] || []);

const pipelineOptions = computed(() =>
  pipelines.value.map(pipeline => ({
    value: pipeline.id,
    label: pipeline.name || `Pipeline #${pipeline.id}`,
  }))
);

const getStagesByPipeline = pipelineId => {
  if (!pipelineId) return [];
  const pipeline = pipelines.value.find(
    item => Number(item.id) === Number(pipelineId)
  );
  if (!pipeline) return [];
  return (pipeline.stages || []).map(stage => ({
    value: stage.id,
    label: stage.name,
  }));
};

const ensurePipelineStages = async pipelineId => {
  if (!pipelineId) return;
  const existing = getStagesByPipeline(pipelineId);
  if (existing.length) return;
  await store.dispatch('pipelines/getStages', {
    accountId: accountId.value,
    pipelineId: Number(pipelineId),
  });
};

const loadDependencies = async () => {
  await store
    .dispatch('pipelines/get', { accountId: accountId.value })
    .catch(() => null);
};

onMounted(() => {
  loadDependencies().catch(() => null);
});

const name = ref('');
const description = ref('');
const active = ref(true);
const loading = ref(false);

const valueOptionsFor = attributeKey => {
  const inputType = conditionAttributeSchema[attributeKey]?.inputType;
  if (inputType === 'select_label') return labelOptions;
  if (inputType === 'select_direction') return directionOptions;
  return [];
};

const makeCondition = (attributeKey = 'to_label') => ({
  attribute_key: attributeKey,
  values: [],
});

const makeAction = () => ({
  action_name: 'add_contact_label',
  labelValue: '',
  webhookUrl: '',
  selectedPipelineId: null,
  selectedStageId: null,
  dealTitle: '',
  activityTitle: '',
  activityDescription: '',
});

const conditions = ref([makeCondition()]);
const actions = ref([makeAction()]);

const onConditionAttributeChange = condition => {
  condition.values = [];
};

const addCondition = () => {
  conditions.value.push(makeCondition());
};

const removeCondition = index => {
  conditions.value.splice(index, 1);
};

const addAction = () => {
  actions.value.push(makeAction());
};

const removeAction = index => {
  actions.value.splice(index, 1);
};

const onActionNameChange = action => {
  const next = makeAction();
  action.labelValue = next.labelValue;
  action.webhookUrl = next.webhookUrl;
  action.selectedPipelineId = next.selectedPipelineId;
  action.selectedStageId = next.selectedStageId;
  action.dealTitle = next.dealTitle;
  action.activityTitle = next.activityTitle;
  action.activityDescription = next.activityDescription;
};

const onActionPipelineChange = async action => {
  action.selectedStageId = null;
  await ensurePipelineStages(action.selectedPipelineId);
};

const validateCondition = condition => {
  if (!condition.attribute_key) return 'Selecione o atributo da condição.';
  if (!condition.values.length) {
    return `Selecione ao menos um valor para "${conditionAttributeSchema[condition.attribute_key]?.label || condition.attribute_key}".`;
  }
  return null;
};

const validateAction = action => {
  if (action.action_name === 'add_contact_label' && !action.labelValue.trim()) {
    return 'Informe a etiqueta na ação "Adicionar etiqueta ao contato".';
  }
  if (
    action.action_name === 'send_webhook_event' &&
    !action.webhookUrl.trim()
  ) {
    return 'Informe a URL na ação "Enviar webhook".';
  }
  if (action.action_name === 'create_deal' && !action.selectedPipelineId) {
    return 'Selecione o pipeline na ação "Criar negócio".';
  }
  return null;
};

const serializeActionParams = action => {
  if (action.action_name === 'add_contact_label') {
    return action.labelValue ? [action.labelValue.trim()] : [];
  }
  if (action.action_name === 'send_webhook_event') {
    return action.webhookUrl ? [action.webhookUrl.trim()] : [];
  }
  if (action.action_name === 'create_deal') {
    if (!action.selectedPipelineId) return [];
    return [
      {
        pipeline_id: Number(action.selectedPipelineId),
        stage_id: action.selectedStageId
          ? Number(action.selectedStageId)
          : undefined,
        title: action.dealTitle ? action.dealTitle.trim() : undefined,
      },
    ];
  }
  if (action.action_name === 'create_activity') {
    if (!action.activityTitle && !action.activityDescription) return [];
    return [
      {
        title: action.activityTitle ? action.activityTitle.trim() : undefined,
        description: action.activityDescription
          ? action.activityDescription.trim()
          : undefined,
      },
    ];
  }
  return [];
};

const submit = async () => {
  const trimmedName = name.value.trim();
  if (!trimmedName) {
    useAlert('Informe o nome da regra.');
    return;
  }

  const conditionError = conditions.value.reduce(
    (acc, c) => acc || validateCondition(c),
    null
  );
  if (conditionError) {
    useAlert(conditionError);
    return;
  }

  const actionError = actions.value.reduce(
    (acc, a) => acc || validateAction(a),
    null
  );
  if (actionError) {
    useAlert(actionError);
    return;
  }

  loading.value = true;
  try {
    const rulePayload = {
      name: trimmedName,
      description: description.value,
      event_name: 'contact_score_crossed',
      active: active.value,
      conditions: conditions.value.map(condition => ({
        attribute_key: condition.attribute_key,
        values: condition.values,
      })),
      actions: actions.value.map(action => ({
        action_name: action.action_name,
        action_params: serializeActionParams(action),
      })),
    };

    await store.dispatch('contactAutomationRules/create', {
      accountId: accountId.value,
      rule: rulePayload,
    });

    useAlert('Regra de automação de contatos criada com sucesso');
    emit('close');
  } catch (error) {
    useAlert(error?.message || 'Erro ao criar regra');
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div>
    <woot-modal-header header-title="Nova automação de contatos" />
    <div class="flex flex-col gap-3 p-4">
      <woot-input
        v-model="name"
        label="Nome da regra"
        type="text"
        placeholder="Ex: Contato virou quente"
      />
      <woot-input
        v-model="description"
        label="Descrição"
        type="text"
        placeholder="Descrição opcional"
      />
      <label>
        Evento
        <select disabled class="m-0">
          <option
            v-for="option in eventOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </label>
      <label class="flex gap-2 items-center">
        <input v-model="active" type="checkbox" />
        Ativa
      </label>

      <div class="p-3 rounded border border-n-weak">
        <div class="flex justify-between items-center mb-2">
          <h4 class="m-0 text-sm font-semibold">Condições</h4>
          <Button xs slate label="Adicionar condição" @click="addCondition" />
        </div>
        <div
          v-for="(condition, index) in conditions"
          :key="`condition-${index}`"
          class="grid grid-cols-1 gap-2 p-2 mb-2 rounded border border-n-weak"
        >
          <label>
            Atributo
            <select
              v-model="condition.attribute_key"
              class="m-0"
              @change="onConditionAttributeChange(condition)"
            >
              <option
                v-for="option in conditionAttributeOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
          <label>
            Valores
            <select v-model="condition.values" multiple class="m-0">
              <option
                v-for="option in valueOptionsFor(condition.attribute_key)"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
          <div class="flex justify-end">
            <Button
              ruby
              xs
              faded
              icon="i-lucide-trash-2"
              @click="removeCondition(index)"
            />
          </div>
        </div>
      </div>

      <div class="p-3 rounded border border-n-weak">
        <div class="flex justify-between items-center mb-2">
          <h4 class="m-0 text-sm font-semibold">Ações</h4>
          <Button xs slate label="Adicionar ação" @click="addAction" />
        </div>
        <div
          v-for="(action, index) in actions"
          :key="`action-${index}`"
          class="grid grid-cols-1 gap-2 p-2 mb-2 rounded border border-n-weak"
        >
          <label>
            Ação
            <select
              v-model="action.action_name"
              class="m-0"
              @change="onActionNameChange(action)"
            >
              <option
                v-for="option in actionOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>

          <woot-input
            v-if="action.action_name === 'add_contact_label'"
            v-model="action.labelValue"
            label="Etiqueta"
            type="text"
            placeholder="Ex: lead-quente"
          />

          <woot-input
            v-else-if="action.action_name === 'send_webhook_event'"
            v-model="action.webhookUrl"
            label="Webhook URL"
            type="text"
            placeholder="Ex: https://api.exemplo.com/webhook"
          />

          <template v-else-if="action.action_name === 'create_deal'">
            <label>
              Pipeline
              <select
                v-model="action.selectedPipelineId"
                class="m-0"
                @change="onActionPipelineChange(action)"
              >
                <option :value="null">Selecione</option>
                <option
                  v-for="pipeline in pipelineOptions"
                  :key="pipeline.value"
                  :value="pipeline.value"
                >
                  {{ pipeline.label }}
                </option>
              </select>
            </label>
            <label>
              Estágio (opcional)
              <select v-model="action.selectedStageId" class="m-0">
                <option :value="null">Estágio inicial</option>
                <option
                  v-for="stage in getStagesByPipeline(
                    action.selectedPipelineId
                  )"
                  :key="stage.value"
                  :value="stage.value"
                >
                  {{ stage.label }}
                </option>
              </select>
            </label>
            <woot-input
              v-model="action.dealTitle"
              label="Título (opcional)"
              type="text"
              placeholder="Título do negócio"
            />
          </template>

          <template v-else-if="action.action_name === 'create_activity'">
            <woot-input
              v-model="action.activityTitle"
              label="Título (opcional)"
              type="text"
              placeholder="Título da atividade"
            />
            <woot-input
              v-model="action.activityDescription"
              label="Descrição (opcional)"
              type="text"
              placeholder="Descrição da atividade"
            />
          </template>

          <div class="flex justify-end">
            <Button
              ruby
              xs
              faded
              icon="i-lucide-trash-2"
              @click="removeAction(index)"
            />
          </div>
        </div>
      </div>

      <div class="flex gap-2 justify-end pt-2">
        <Button color="slate" label="Cancelar" @click="emit('close')" />
        <Button :is-loading="loading" label="Salvar regra" @click="submit" />
      </div>
    </div>
  </div>
</template>
